<template>
	<main class="h-screen overflow-y-auto bg-white p-4">
		<div v-if="isLoading" class="flex items-center justify-center py-10">
			<i class="tabler:loader-2 w-8 h-8 animate-spin text-primary"></i>
			<span class="ml-2">Loading...</span>
		</div>
		<div v-else-if="fetchError" class="p-4 bg-red-50 border-l-4 border-red-400 rounded-r-lg">
			<div class="flex items-center">
				<i class="tabler:alert-circle text-red-600 text-lg mr-2"></i>
				<span class="text-red-800">{{ fetchError }}</span>
			</div>
		</div>
		<div v-else-if="!keyMessagesContent" class="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
			<div class="flex items-center">
				<i class="tabler:alert-triangle text-yellow-700 text-lg mr-2"></i>
				<span class="text-yellow-900">No Key Messages found.</span>
			</div>
		</div>
		<div v-else id="editKeyMessagges" class="key-messages-frame">
			<div id="editKeyMessaggesWrapper" v-html="keyMessagesContent"></div>
		</div>
	</main>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

const props = defineProps<{
	sourceUrl?: string | string[]
}>()

const sourceUrl = computed(() => {
	const value = Array.isArray(props.sourceUrl) ? props.sourceUrl[0] : props.sourceUrl
	return String(value ?? '').trim()
})

const isLoading = ref(false)
const fetchError = ref<string | null>(null)
const keyMessagesContent = ref('')

const parseKeyMessagesResponse = (responseText: string) => {
	const trimmedText = responseText.trim().replace(/^\uFEFF/, '')
	const parsed = JSON.parse(trimmedText)
	return typeof parsed === 'string' ? JSON.parse(parsed.trim().replace(/^\uFEFF/, '')) : parsed
}

const resolveKeyMessagesContent = (data: any) =>
	String(
		data?.other?.keyMessages ??
		data?.mainOutcome?.keyMessages ??
		data?.keyMessages ??
		data?.other?.keymessages ??
		''
	).trim()

const fetchKeyMessages = async () => {
	if (!sourceUrl.value) {
		return
	}

	isLoading.value = true
	fetchError.value = null
	keyMessagesContent.value = ''

	try {
		const response = await fetch(sourceUrl.value, {
			method: 'GET',
			credentials: 'include',
			headers: {
				Accept: 'application/json',
			},
		})

		if (!response.ok) {
			fetchError.value = `Unable to load Key Messages (${response.status}): ${sourceUrl.value}`
			return
		}

		const data = parseKeyMessagesResponse(await response.text())
		keyMessagesContent.value = resolveKeyMessagesContent(data)
	} catch (error) {
		fetchError.value = `Unable to load Key Messages: ${error instanceof Error ? error.message : String(error)} (${sourceUrl.value})`
	} finally {
		isLoading.value = false
	}
}

watch(sourceUrl, fetchKeyMessages, { immediate: true })
</script>

<style scoped>
.key-messages-frame {
	color: #1f2937;
	font-size: 0.95rem;
	line-height: 1.65;
}

.key-messages-frame :deep(ol) {
	list-style: decimal-leading-zero;
	margin: 0 1em;
	padding: 0 20px;
}

.key-messages-frame :deep(ul),
.key-messages-frame :deep(menu) {
	list-style: disc;
	margin: 0 1em;
	padding: 0 20px;
}
</style>
